'use client';

import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './tooltip';
import { Popover, PopoverTrigger, PopoverContent } from './popover';
import { TooltipContentProps, TooltipProps, TooltipTriggerProps } from '@radix-ui/react-tooltip';
import { PopoverContentProps, PopoverProps, PopoverTriggerProps } from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';

const TouchContext = createContext<boolean>(false);
const useTouch = () => useContext(TouchContext);

export const TouchProvider = (props: PropsWithChildren) => {
  const [isTouch, setTouch] = useState<boolean>(false);

  useEffect(() => {
    // Set initial value
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    setTouch(mediaQuery.matches);

    // Add listener for changes
    const handleChange = (e: MediaQueryListEvent) => {
      setTouch(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return <TouchContext.Provider value={isTouch} {...props} />;
};

export const HybridTooltip = (props: TooltipProps & PopoverProps) => {
  const isTouch = useTouch();

  return isTouch ? (
    <Popover {...props} />
  ) : (
    <TooltipProvider>
      <Tooltip {...props} />
    </TooltipProvider>
  );
};

export const HybridTooltipTrigger = (props: TooltipTriggerProps & PopoverTriggerProps) => {
  const isTouch = useTouch();

  return isTouch ? <PopoverTrigger {...props} /> : <TooltipTrigger {...props} />;
};

export const HybridTooltipContent = (
  props: TooltipContentProps &
    PopoverContentProps & {
      popoverClassName?: string;
      tooltipClassName?: string;
    }
) => {
  const isTouch = useTouch();
  const defaultPopoverClasses =
    "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

  const { popoverClassName, tooltipClassName, className, ...restProps } = props as any;

  if (isTouch) {
    return (
      <PopoverContent
        className={cn(defaultPopoverClasses, popoverClassName, className)}
        {...(restProps as any)}
      />
    );
  }

  return (
    <TooltipContent
      className={cn(tooltipClassName, className)}
      {...(restProps as any)}
    />
  );
};